/**
 * CotacaoViewDialog - Modal de visualização de uma cotação (extraído de Compras.jsx).
 * Componente de apresentação; recebe a cotação, anexos e handlers via props.
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ChevronDown } from 'lucide-react';
import { StatusBadge, formatCurrency as _fmtCurrency } from './utils';
import { getCurrencyMeta } from '../../lib/currencies';
import AnexosManager from '../AnexosManager';
import AnexosErrorBoundary from '../AnexosErrorBoundary';

const CotacaoViewDialog = ({
  cotacao,
  onClose,
  expandedFornecedor,
  setExpandedFornecedor,
  anexos,
  onUploadAnexo,
  onDeleteAnexo,
  onPatchAnexo,
}) => {
  const formatCurrency = (v) => _fmtCurrency(v, cotacao?.moeda || 'BRL');
  return (
    <Dialog open={!!cotacao} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono text-blue-600">{cotacao?.numero}</span>
            <StatusBadge status={cotacao?.status} />
            {cotacao?.moeda && cotacao.moeda !== 'BRL' && (
              <span className="text-xs px-2 py-0.5 rounded-full border border-indigo-300 text-indigo-700" data-testid="view-cotacao-moeda">
                {getCurrencyMeta(cotacao.moeda).symbol} {cotacao.moeda}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {cotacao && (
          <div className="space-y-6 mt-4">
            <div>
              <h3 className="font-semibold text-lg">{cotacao.titulo}</h3>
              {cotacao.descricao && (
                <div className="text-gray-600 mt-1 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: cotacao.descricao }} />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Empresa</p>
                <p className="font-medium">{cotacao.empresa_razao_social}</p>
              </div>
              <div>
                <p className="text-gray-500">Data Limite</p>
                <p className="font-medium">
                  {cotacao.data_limite_resposta
                    ? new Date(cotacao.data_limite_resposta).toLocaleDateString('pt-BR')
                    : 'Não definida'}
                </p>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-3">Itens ({cotacao.itens?.length || 0})</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3">#</th>
                      <th className="text-left p-3">Descrição</th>
                      <th className="text-center p-3">Qtd</th>
                      <th className="text-center p-3">Unid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cotacao.itens?.map((item, idx) => (
                      <tr key={item.id} className="border-t">
                        <td className="p-3 text-gray-500">{idx + 1}</td>
                        <td className="p-3">
                          <p className="font-medium">{item.descricao}</p>
                          {(item.marca || item.modelo) && (
                            <p className="text-gray-500 text-xs">{[item.marca, item.modelo].filter(Boolean).join(' - ')}</p>
                          )}
                        </td>
                        <td className="p-3 text-center">{item.quantidade}</td>
                        <td className="p-3 text-center">{item.unidade}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-3">Fornecedores ({cotacao.fornecedores?.length || 0})</h4>
              <div className="space-y-2">
                {cotacao.fornecedores?.map(forn => {
                  const isExpanded = expandedFornecedor === forn.fornecedor_id;
                  const resp = forn.resposta;
                  return (
                    <div key={forn.fornecedor_id} className="bg-gray-50 rounded-lg overflow-hidden">
                      <div
                        className={`flex items-center justify-between p-3 ${resp ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                        onClick={() => resp && setExpandedFornecedor(isExpanded ? null : forn.fornecedor_id)}
                        data-testid={`fornecedor-row-${forn.fornecedor_id}`}
                      >
                        <div className="flex items-center gap-2">
                          {resp && (
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          )}
                          <div>
                            <p className="font-medium">{forn.fornecedor_nome}</p>
                            <p className="text-sm text-gray-500">{forn.contato_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {resp && (
                            <span className="text-sm font-medium text-green-700">
                              {formatCurrency(resp.total || resp.valor_total || 0)}
                            </span>
                          )}
                          <Badge className={
                            forn.status === 'respondido' ? 'bg-green-100 text-green-700' :
                            forn.status === 'enviado' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }>
                            {forn.status === 'respondido' ? 'Respondido' : forn.status === 'enviado' ? 'Enviado' : 'Pendente'}
                          </Badge>
                        </div>
                      </div>
                      {isExpanded && resp && (
                        <div className="px-3 pb-3 border-t border-gray-200" data-testid={`fornecedor-resposta-${forn.fornecedor_id}`}>
                          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <p className="text-xs text-gray-500">Frete (CIF/FOB)</p>
                              <p className="font-medium">{resp.frete_tipo === 'cif' ? 'CIF (Incluso)' : resp.frete_tipo === 'fob' ? 'FOB' : resp.frete_tipo || '-'} {resp.frete_valor > 0 ? `- ${formatCurrency(resp.frete_valor)}` : ''}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Forma de Pagamento</p>
                              <p className="font-medium">{resp.forma_pagamento || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Condição/Parcelas</p>
                              <p className="font-medium">{resp.condicao_pagamento || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Impostos Inclusos</p>
                              <p className="font-medium">{resp.valor_impostos ? formatCurrency(resp.valor_impostos) : '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Prazo de Entrega</p>
                              <p className="font-medium">{resp.prazo_entrega || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Prazo Garantia</p>
                              <p className="font-medium">{resp.prazo_garantia || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Assistência Técnica</p>
                              <p className="font-medium">{resp.assistencia_tecnica || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Total</p>
                              <p className="font-medium text-green-700">{formatCurrency(resp.total || resp.valor_total || 0)}</p>
                            </div>
                          </div>
                          {resp.observacao && (
                            <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                              <p className="text-xs text-gray-500 mb-1">Observações</p>
                              <p className="text-gray-800">{resp.observacao}</p>
                            </div>
                          )}
                          {resp.itens?.length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs text-gray-500 mb-1">Itens</p>
                              <div className="border rounded overflow-hidden">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="text-left p-2">Item</th>
                                      <th className="text-center p-2 w-16">Qtd</th>
                                      <th className="text-right p-2 w-28">Unit.</th>
                                      <th className="text-right p-2 w-28">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {resp.itens.map((item, idx) => (
                                      <tr key={idx} className="border-t">
                                        <td className="p-2">{item.descricao}</td>
                                        <td className="p-2 text-center">{item.quantidade}</td>
                                        <td className="p-2 text-right">{formatCurrency(item.preco_unitario || 0)}</td>
                                        <td className="p-2 text-right font-medium">{formatCurrency((item.preco_unitario || 0) * (item.quantidade || 1))}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Seção de Anexos - padronizado com AnexosManager */}
            <div>
              <AnexosErrorBoundary>
                <AnexosManager
                  anexos={(anexos || []).map(a => ({
                    id: a.id,
                    nome: a.nome || a.filename,
                    nome_exibicao: a.nome_exibicao || (a.nome || a.filename || '').replace(/\.[^/.]+$/, ''),
                    descricao: a.descricao || '',
                    url: a.download_url || a.url,
                    tipo: a.tipo || a.content_type,
                    tamanho: a.tamanho || a.size,
                    data_upload: a.uploaded_at || a.data_upload,
                    uploaded_at: a.uploaded_at,
                    uploaded_by: a.uploaded_by,
                    s3_key: a.s3_key,
                    item_id: a.item_id || '',
                  }))}
                  onChange={() => { /* server-side managed */ }}
                  maxFiles={30}
                  maxSizeMB={50}
                  label="Anexos da Cotação (galeria)"
                  modulo="compras_cotacoes"
                  entidadeId={cotacao.id}
                  allowDescription
                  itensCotacao={(cotacao.itens || []).map(it => ({ id: it.id, descricao: it.descricao }))}
                  uploadHandler={async (file, itemId) => {
                    await onUploadAnexo(cotacao.id, file, itemId);
                    return null;
                  }}
                  deleteHandler={async (anexo) => {
                    await onDeleteAnexo(cotacao.id, anexo.id);
                  }}
                  metadataUpdateHandler={async (anexo, patch) => {
                    await onPatchAnexo(cotacao.id, anexo.id, patch);
                  }}
                  vincularItemHandler={async (anexo, item_id) => {
                    await onPatchAnexo(cotacao.id, anexo.id, { item_id });
                  }}
                />
              </AnexosErrorBoundary>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose()}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CotacaoViewDialog;
